import type { ReactNode } from 'react';
import { Badge, Box, InlineStack, Link, Stack } from '@customerio/pluma-components/react';
import { AttributeTypeLabel, type IdentifiersConfig } from '@/features/people';
import { AutogeneratedAttributeTooltip } from './autogenerated-attribute-tooltip';
import { shouldProcessLinkClick } from '@/utils/navigation';
import { RowClickGuard } from './row-click-guard';

/**
 * A row's name, what is known about it, and its description.
 *
 * The name opens the row's details beside the list, which is the quick
 * answer; it is still a real link to the item's own page, so a modifier
 * click opens that in a tab rather than doing nothing. The description sits
 * under it and is edited in place, because it is the one thing about a row
 * that the workspace writes rather than the ingestion.
 */

export type NameCellProps = {
  name: string;
  href: string;
  isSensitive: boolean;
  isAutogenerated: boolean;
  /** Only person attributes carry a Reserved or Identifier badge. */
  identifiers: IdentifiersConfig | null | undefined;
  /** Whether the workspace identifies people by more than one attribute. */
  isMultiIdentifier: boolean;
  /** The workspace whose identifier settings the badge's tooltip links to. */
  workspaceId: number;
  withAttributeBadges: boolean;
  /** The description, which the row's own editor renders. */
  description: ReactNode;
  /** Opens this row's details beside the list. */
  onOpen: () => void;
};

export function NameCell({
  name,
  href,
  isSensitive,
  isAutogenerated,
  identifiers,
  isMultiIdentifier,
  workspaceId,
  withAttributeBadges,
  description,
  onOpen,
}: NameCellProps) {
  return (
    <Stack>
      <InlineStack alignBlock="center" gap="075">
        <RowClickGuard>
          <Link
            variant="primary"
            href={href}
            truncate
            onClick={(event: React.MouseEvent) => {
              // A modifier click is a request for a tab, so leave it to the
              // browser; a plain one opens the details beside the list. The
              // predicate is the app's own, so Option-click and `_blank`
              // count as the browser's too.
              if (!shouldProcessLinkClick(event)) {
                return;
              }
              event.preventDefault();
              onOpen();
            }}
          >
            {name}
          </Link>
        </RowClickGuard>
        {isAutogenerated ? <AutogeneratedAttributeTooltip attribute={name} /> : null}
        {withAttributeBadges ? (
          <AttributeTypeLabel
            attribute={name}
            identifiers={identifiers}
            identifierBadge={{ isMultiIdentifier, workspaceId: String(workspaceId) }}
          />
        ) : null}
        {isSensitive ? <Badge color="blue">Sensitive</Badge> : null}
      </InlineStack>
      {/* Nothing between the two lines: the page this replaces stacks the
          name and the description in one rhythm, and 4px here made every
          row 63px against its 59px (`name-cell/index.gts:215`). */}
      <Box>{description}</Box>
    </Stack>
  );
}
